import {
  ANDAData,
  CompetitorData,
  CompetitorsAndaRequestParams,
  DateRange,
  MarketSalesTrendItem,
  TimelineEntry,
} from 'Interfaces/ProductWikiInterfaces/competitors.types';
import { AIInsightItem } from 'Interfaces/ProductWikiInterfaces/aiInsights.types';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { Calendar } from 'primereact/calendar';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import { productWikiServices } from 'services/productWikiServices';

// Interface for timeline point positions (for hover detection)
interface TimelinePoint {
  x: number;
  y: number;
  radius: number;
  event: TimelineEntry;
}

/** Map PrimeReact ANDA filter state to API request params (server-side filters) */
function getAndaFilterParams(
  filters: Record<string, unknown>
): Omit<CompetitorsAndaRequestParams, 'gfc' | 'page' | 'page_size'> {
  const f = filters as Record<string, { value?: unknown; matchMode?: string }>;
  const v = (key: string): string | undefined => {
    const o = f[key];
    if (o?.value == null || o.value === '') return undefined;
    if (typeof o.value === 'string') return o.value.trim() || undefined;
    return undefined;
  };
  const dateRange = f.lastSubmissionDate?.value as [Date | null, Date | null] | null | undefined;
  let last_submission_date_from: string | undefined;
  let last_submission_date_to: string | undefined;
  if (dateRange && Array.isArray(dateRange)) {
    if (dateRange[0]) last_submission_date_from = dateRange[0].toISOString().slice(0, 10);
    if (dateRange[1]) last_submission_date_to = dateRange[1].toISOString().slice(0, 10);
  }
  return {
    application_number: v('applicationNumber'),
    market_status: v('marketStatus'),
    active_ingredient: v('activeIngredient'),
    dosage_form: v('dosageForm'),
    route: v('route'),
    strength: v('strength'),
    rld: v('rld'),
    rs: v('rs'),
    applicant_holder: v('applicantHolder'),
    last_submission_type: v('lastSubmissionType'),
    last_submission_date_from,
    last_submission_date_to,
  };
}

const useCompetitorsTab = (
  productAnalysisStore: ProductAnalysisStore,
  competitorData: CompetitorData,
  andaData: ANDAData
) => {
  const andaTimelineRef = useRef<HTMLCanvasElement>(null);
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [andaFilters, setAndaFilters] = useState<Record<string, unknown>>({});
  const abortControllerRef = useRef<AbortController | null>(null);
  const previousGfcRef = useRef<string | null>(null);
  const andaFilterFirstRunRef = useRef<boolean>(true);

  // Timeline tooltip state
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    x: number;
    y: number;
    event: TimelineEntry | null;
  }>({ visible: false, x: 0, y: 0, event: null });
  const timelinePointsRef = useRef<TimelinePoint[]>([]);

  // AI Insights state
  const [aiInsights, setAiInsights] = useState<AIInsightItem[] | null>(null);
  const [isInsightsLoading, setIsInsightsLoading] = useState<boolean>(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const insightsAbortControllerRef = useRef<AbortController | null>(null);

  // Chart state for Market Sales Trends
  const [chartDateRange, setChartDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });
  const [tempDateRange, setTempDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });
  const [calendarViewDates, setCalendarViewDates] = useState<{
    startViewDate: Date | null;
    endViewDate: Date | null;
  }>({ startViewDate: null, endViewDate: null });
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

  // Get GFC from URL params
  const gfc = searchParams.get('gfc');

  // === SPLIT API DATA ACCESS ===
  // Access section-specific data from store
  const overviewStore = productAnalysisStore.competitorsOverviewData;
  const andaStore = productAnalysisStore.competitorsAndaData;
  const marketChartsStore = productAnalysisStore.competitorsMarketChartsData;

  // Section-specific loading states
  const isOverviewLoading = productAnalysisStore.isCompetitorsOverviewLoading;
  const isAndaLoading = productAnalysisStore.isCompetitorsAndaLoading;
  const isMarketChartsLoading = productAnalysisStore.isCompetitorsMarketChartsLoading;

  // Legacy store data (for backward compatibility)
  const competitorsStore = productAnalysisStore.competitorsData;
  const storeGfc = productAnalysisStore.currentGfc;
  const currentGfc = gfc?.trim() || null;
  const storeDataMatchesGfc = storeGfc === currentGfc;
  const isLoading = productAnalysisStore.isCompetitorsLoading;

  // === Determine which data source to use (split API preferred) ===
  // Overview section: barriers, timeline, manufacturers
  const hasOverviewData = overviewStore && storeDataMatchesGfc;
  const _shouldIgnoreOverview = isOverviewLoading || !storeDataMatchesGfc;

  // ANDA section: applications + pagination
  const hasAndaStoreData = andaStore && storeDataMatchesGfc;
  const _shouldIgnoreAnda = isAndaLoading || !storeDataMatchesGfc;

  // Market charts section: sales trend, market share
  const hasMarketChartsData = marketChartsStore && storeDataMatchesGfc;
  const _shouldIgnoreMarketCharts = isMarketChartsLoading || !storeDataMatchesGfc;

  // Overall loading state (for full-page loader during initial load)
  const shouldIgnoreData = isLoading || !storeDataMatchesGfc;

  // Effective competitor data (from split API or legacy)
  const effectiveCompetitorData = useMemo(() => {
    // Prefer split API data
    if (hasOverviewData && overviewStore?.competitors) {
      return overviewStore.competitors;
    }
    // Fallback to legacy store data
    if (!shouldIgnoreData && storeDataMatchesGfc && competitorsStore?.competitors) {
      return competitorsStore.competitors;
    }
    // Fallback to props if not loading
    if (!shouldIgnoreData) {
      return competitorData;
    }
    return null;
  }, [hasOverviewData, overviewStore, shouldIgnoreData, storeDataMatchesGfc, competitorsStore, competitorData]);

  // Effective timeline data (from split API or legacy)
  const effectiveTimeline: TimelineEntry[] = useMemo(() => {
    // Prefer split API data
    if (hasOverviewData && overviewStore?.timeline) {
      return overviewStore.timeline;
    }
    // Fallback to legacy store data
    if (!shouldIgnoreData && storeDataMatchesGfc && competitorsStore?.anda?.timeline) {
      return competitorsStore.anda.timeline;
    }
    // Fallback to props
    if (!shouldIgnoreData && andaData?.timeline) {
      return andaData.timeline;
    }
    return [];
  }, [hasOverviewData, overviewStore, shouldIgnoreData, storeDataMatchesGfc, competitorsStore, andaData]);

  // Effective ANDA data (from split API or legacy)
  const effectiveAndaData = useMemo(() => {
    // Prefer split API data
    if (hasAndaStoreData && andaStore?.anda) {
      // Reconstruct ANDAData format with timeline from overview
      return {
        applications: andaStore.anda.applications,
        pagination: andaStore.anda.pagination,
        timeline: effectiveTimeline,
      } as ANDAData;
    }
    // Fallback to legacy store data
    if (!shouldIgnoreData && storeDataMatchesGfc && competitorsStore?.anda) {
      return competitorsStore.anda;
    }
    // Fallback to props
    if (!shouldIgnoreData) {
      return andaData;
    }
    return null;
  }, [hasAndaStoreData, andaStore, effectiveTimeline, shouldIgnoreData, storeDataMatchesGfc, competitorsStore, andaData]);

  // Market sales trend data from split API or legacy
  const allMarketSalesTrendData = useMemo(() => {
    // Prefer split API data
    if (hasMarketChartsData && marketChartsStore?.marketSalesTrend) {
      return marketChartsStore.marketSalesTrend;
    }
    // Fallback to legacy store data
    if (!shouldIgnoreData && storeDataMatchesGfc && competitorsStore?.marketSalesTrend) {
      return competitorsStore.marketSalesTrend;
    }
    return [];
  }, [hasMarketChartsData, marketChartsStore, shouldIgnoreData, storeDataMatchesGfc, competitorsStore]);

  // Market sales trend data by labeler (for filtering by selected company)
  const marketSalesTrendByLabeler = useMemo(() => {
    // Prefer split API data
    if (hasMarketChartsData && marketChartsStore?.marketSalesTrendByLabeler) {
      return marketChartsStore.marketSalesTrendByLabeler;
    }
    // Fallback to legacy store data
    if (!shouldIgnoreData && storeDataMatchesGfc && competitorsStore?.marketSalesTrendByLabeler) {
      return competitorsStore.marketSalesTrendByLabeler;
    }
    return {};
  }, [hasMarketChartsData, marketChartsStore, shouldIgnoreData, storeDataMatchesGfc, competitorsStore]);

  // Effective market sales trend data - use per-labeler data when a company is selected
  const marketSalesTrendData = useMemo(() => {
    if (selectedCompany && marketSalesTrendByLabeler[selectedCompany]) {
      return marketSalesTrendByLabeler[selectedCompany];
    }
    return allMarketSalesTrendData;
  }, [selectedCompany, allMarketSalesTrendData, marketSalesTrendByLabeler]);

  // Market share by volume data from split API or legacy
  const marketShareByVolume = useMemo(() => {
    // Prefer split API data
    if (hasMarketChartsData && marketChartsStore?.marketShareByVolume) {
      return marketChartsStore.marketShareByVolume;
    }
    // Fallback to legacy store data
    if (!shouldIgnoreData && storeDataMatchesGfc && competitorsStore?.marketShareByVolume) {
      return competitorsStore.marketShareByVolume;
    }
    return [];
  }, [hasMarketChartsData, marketChartsStore, shouldIgnoreData, storeDataMatchesGfc, competitorsStore]);

  // Apply date filter to market sales trend data
  const applyDateFilter = useCallback(
    (data: MarketSalesTrendItem[], dateRange: DateRange): MarketSalesTrendItem[] => {
      if (!dateRange.startDate && !dateRange.endDate) {
        return data;
      }

      const startDate = dateRange.startDate
        ? (() => {
            const d = new Date(
              dateRange.startDate.getFullYear(),
              dateRange.startDate.getMonth(),
              1
            );
            d.setHours(0, 0, 0, 0);
            return d;
          })()
        : null;
      const endDate = dateRange.endDate
        ? (() => {
            const d = new Date(dateRange.endDate.getFullYear(), dateRange.endDate.getMonth(), 1);
            d.setHours(0, 0, 0, 0);
            return d;
          })()
        : null;

      const startTime = startDate ? startDate.getTime() : null;
      const endTime = endDate ? endDate.getTime() : null;

      const filtered = data.filter(item => {
        const parts = item.month.split('-');
        if (parts.length !== 2) return true;

        const monthNames: Record<string, number> = {
          jan: 0,
          feb: 1,
          mar: 2,
          apr: 3,
          may: 4,
          jun: 5,
          jul: 6,
          aug: 7,
          sept: 8,
          oct: 9,
          nov: 10,
          dec: 11,
        };

        const monthStr = parts[0].toLowerCase();
        const month = monthNames[monthStr];
        if (month === undefined) return true;

        const yearShort = parseInt(parts[1], 10);
        if (isNaN(yearShort)) return true;
        const year = 2000 + yearShort;

        const itemDate = new Date(year, month, 1);
        itemDate.setHours(0, 0, 0, 0);
        const itemTime = itemDate.getTime();

        if (startTime !== null && itemTime < startTime) {
          return false;
        }
        if (endTime !== null && itemTime > endTime) {
          return false;
        }

        return true;
      });

      return filtered;
    },
    []
  );

  // Filtered market sales trend data based on date range
  const filteredMarketSalesTrendData = useMemo(() => {
    if (chartDateRange.startDate || chartDateRange.endDate) {
      return applyDateFilter(marketSalesTrendData, chartDateRange);
    }
    return marketSalesTrendData;
  }, [marketSalesTrendData, chartDateRange, applyDateFilter]);

  // Format month string for chart labels (e.g., "jan-25" -> "Jan 25")
  const formatMonth = (monthStr: string): string => {
    const parts = monthStr.split('-');
    if (parts.length !== 2) return monthStr;
    const monthName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    return `${monthName} '${parts[1]}`;
  };

  // Transform market sales trend data into Chart.js format
  const getMarketTrendsChartData = useCallback(
    (
      marketData: MarketSalesTrendItem[],
      filteredData: MarketSalesTrendItem[],
      dateRange: DateRange
    ) => {
      const hasDateFilter = !!(dateRange.startDate || dateRange.endDate);

      let dataToUse: MarketSalesTrendItem[];
      if (hasDateFilter) {
        if (filteredData.length > 0) {
          dataToUse = filteredData;
        } else if (marketData.length > 0) {
          dataToUse = applyDateFilter(marketData, dateRange);
        } else {
          dataToUse = [];
        }
      } else {
        dataToUse = marketData;
      }

      if (!dataToUse || dataToUse.length === 0) {
        return { labels: [], datasets: [] };
      }

      return {
        labels: dataToUse.map(d => formatMonth(d.month)),
        datasets: [
          {
            type: 'bar' as const,
            label: 'DOD Volume',
            data: dataToUse.map(d => d.dod_volume || 0),
            backgroundColor: '#5b5fd6',
            borderColor: '#5b5fd6',
            borderWidth: 1,
            yAxisID: 'y',
            order: 2,
            stack: 'volume',
          },
          {
            type: 'bar' as const,
            label: 'VA Volume',
            data: dataToUse.map(d => d.va_volume || 0),
            backgroundColor: '#00bcd4',
            borderColor: '#00bcd4',
            borderWidth: 1,
            yAxisID: 'y',
            order: 2,
            stack: 'volume',
          },
          {
            type: 'line' as const,
            label: 'Sales',
            data: dataToUse.map(d => d.sales || 0),
            borderColor: '#f59e0b',
            backgroundColor: '#f59e0b',
            borderWidth: 2,
            fill: false,
            tension: 0,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: '#f59e0b',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            yAxisID: 'y1',
            order: 1,
          },
        ],
      };
    },
    [applyDateFilter]
  );

  // Chart data computed from market sales trend
  const marketTrendsChartData = useMemo(
    () =>
      getMarketTrendsChartData(marketSalesTrendData, filteredMarketSalesTrendData, chartDateRange),
    [marketSalesTrendData, filteredMarketSalesTrendData, chartDateRange, getMarketTrendsChartData]
  );

  // Handler for date range changes
  const handleDateRangeChange = useCallback((startDate: Date | null, endDate: Date | null) => {
    setTempDateRange({ startDate, endDate });
  }, []);

  // Handler for applying date filter
  const handleApplyDateFilter = useCallback(() => {
    setChartDateRange(tempDateRange);
  }, [tempDateRange]);

  // Handler for calendar view date changes
  const handleCalendarViewDateChange = useCallback(
    (dates: { startViewDate: Date | null; endViewDate: Date | null }) => {
      setCalendarViewDates(dates);
    },
    []
  );

  // Handler for market share company click
  const handleMarketShareClick = useCallback((vendor: string) => {
    setSelectedCompany(prevSelected => (prevSelected === vendor ? null : vendor));
  }, []);

  // Generic date range filter function (same as in DataModal)
  const dateRangeFilterFunction = (value: string | Date | null | undefined, filters: [Date | null, Date | null] | null) => {
    const [from, to] = filters ?? [null, null];
    if (from === null && to === null) return true;

    // Handle string dates
    let valueDate: Date | null = null;
    if (value) {
      if (typeof value === 'string') {
        valueDate = new Date(value);
      } else if (value instanceof Date) {
        valueDate = value;
      }
    }

    if (!valueDate || isNaN(valueDate.getTime())) return false;

    if (from !== null && to === null) {
      const fromDate = new Date(from);
      fromDate.setHours(0, 0, 0, 0);
      valueDate.setHours(0, 0, 0, 0);
      return fromDate <= valueDate;
    }
    if (from === null && to !== null) {
      const toDate = new Date(to);
      toDate.setHours(23, 59, 59, 999);
      valueDate.setHours(23, 59, 59, 999);
      return valueDate <= toDate;
    }

    // Both from and to are non-null here
    const fromDate = new Date(from as Date);
    fromDate.setHours(0, 0, 0, 0);
    const toDate = new Date(to as Date);
    toDate.setHours(23, 59, 59, 999);
    valueDate.setHours(0, 0, 0, 0);

    return fromDate <= valueDate && valueDate <= toDate;
  };

  // Fetch data when component mounts or GFC changes
  // Uses split API endpoints for parallel loading
  useEffect(() => {
    // Abort previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const currentGfc = gfc?.trim() || null;

    if (currentGfc) {
      // Check if this is the first mount (previousGfcRef is null)
      const isFirstMount = previousGfcRef.current === null;

      // Check if GFC has changed from previous render
      const gfcChanged = !isFirstMount && previousGfcRef.current !== currentGfc;

      // Check if store data belongs to a different GFC (using store's currentGfc tracking)
      const storeGfc = productAnalysisStore.currentGfc;
      const storeGfcMismatch = storeGfc !== null && storeGfc !== currentGfc;

      // Check if split API data exists
      const hasSplitData = overviewStore || andaStore || marketChartsStore;

      // Always fetch when:
      // 1. First mount (component just loaded) - always fetch to ensure correct data
      // 2. GFC changes (switching to a new concept)
      // 3. Store has data for a different GFC (mismatch) - prevents showing wrong data
      // 4. No data exists in store (neither split nor legacy)
      // This ensures fresh data when switching concepts and prevents showing wrong data
      if (isFirstMount || gfcChanged || storeGfcMismatch || (!competitorsStore && !hasSplitData)) {
        // Clear old data when GFC changes, mismatches, or on first mount to ensure we show loader and fetch fresh data
        if (isFirstMount || gfcChanged || storeGfcMismatch) {
          productAnalysisStore.clearCompetitorsData();
        }

        // Create new abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Fetch all competitors data using split endpoints in parallel
        // This provides independent loading states for each section
        productAnalysisStore.fetchAllCompetitorsData(currentGfc, abortController.signal);
      }
    }

    // Update previous GFC reference
    previousGfcRef.current = currentGfc;

    // Cleanup: abort request when effect re-runs or component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gfc]); // Only depend on gfc, not competitorsStore to avoid infinite loops

  // Fetch AI Insights for competitive intelligence
  const fetchAIInsights = useCallback(async () => {
    if (!gfc || gfc.trim() === '') {
      setAiInsights(null);
      return;
    }

    // Abort previous insights request if it exists
    if (insightsAbortControllerRef.current) {
      insightsAbortControllerRef.current.abort();
      insightsAbortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    insightsAbortControllerRef.current = abortController;

    setIsInsightsLoading(true);
    setInsightsError(null);

    try {
      const response = await productWikiServices.generateAIInsights(
        {
          insight_type: 'market_share',
          gfc: gfc.trim(),
          period_option: '12 months',
          llm_backend: 'anthropic'
        },
        abortController.signal
      );

      if (response.data?.success && response.data?.insights) {
        setAiInsights(response.data.insights);
      } else {
        setAiInsights(null);
        if (response.data?.error || response.data?.message) {
          // Combine error and message for a complete error display
          const errorParts: string[] = [];
          if (response.data.error) errorParts.push(response.data.error);
          if (response.data.message && response.data.message !== response.data.error) {
            errorParts.push(response.data.message);
          }
          setInsightsError(errorParts.join(' - ') || 'Failed to load insights');
        }
      }
    } catch (error) {
      // Don't set error if request was aborted
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      setInsightsError('Failed to load AI insights');
      setAiInsights(null);
    } finally {
      setIsInsightsLoading(false);
    }
  }, [gfc]);

  // Effect to fetch AI insights when GFC changes
  useEffect(() => {
    fetchAIInsights();

    // Cleanup: abort request when effect re-runs or component unmounts
    return () => {
      if (insightsAbortControllerRef.current) {
        insightsAbortControllerRef.current.abort();
        insightsAbortControllerRef.current = null;
      }
    };
  }, [fetchAIInsights]);

  // === Section-specific loading states ===
  // Overview section: show loader only if loading and no overview data available
  const hasOverviewDataAvailable =
    effectiveCompetitorData?.manufacturers && effectiveCompetitorData.manufacturers.length > 0;
  const shouldShowOverviewLoader = isOverviewLoading && !hasOverviewDataAvailable;

  // ANDA section: show loader only if loading and no ANDA data available
  const hasAndaDataAvailable =
    effectiveAndaData?.applications && effectiveAndaData.applications.length > 0;
  const shouldShowAndaLoader = isAndaLoading && !hasAndaDataAvailable;

  // Market charts section: show loader only if loading and no chart data available
  const hasMarketChartsDataAvailable = allMarketSalesTrendData.length > 0 || marketShareByVolume.length > 0;
  const shouldShowMarketChartsLoader = isMarketChartsLoading && !hasMarketChartsDataAvailable;

  // Overall loading state (for full-page loader during initial load - all sections loading)
  const hasDataAvailable = hasOverviewDataAvailable || hasAndaDataAvailable;
  const allSectionsLoading = isOverviewLoading && isAndaLoading && isMarketChartsLoading;
  const shouldShowLoader = allSectionsLoading && !hasDataAvailable;

  // Get pagination info from split API store data or legacy
  const pagination = andaStore?.anda?.pagination || competitorsStore?.anda?.pagination;
  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.total_pages || 1;
  const totalCount = pagination?.total_count || effectiveAndaData?.applications?.length || 0;
  const pageSize = pagination?.page_size || 10;

  // Calculate display range
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Check if there's competitor data
  const hasCompetitorData =
    effectiveCompetitorData?.manufacturers && effectiveCompetitorData.manufacturers.length > 0;

  // Check if there's ANDA data
  const hasAndaData = effectiveAndaData?.applications && effectiveAndaData.applications.length > 0;

  // Initialize filters for DataTable
  useEffect(() => {
    const initialFilters: Record<string, { value: unknown; matchMode: string }> = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    // Register date filter for entryDate
    FilterService.register('custom_entryDate', dateRangeFilterFunction);

    // Initialize filters for each column
    initialFilters['name'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialFilters['labeler'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialFilters['ndcCount'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialFilters['price'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialFilters['ranking'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialFilters['averageWac'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialFilters['entryDate'] = { value: null, matchMode: FilterMatchMode.CUSTOM };

    setFilters(initialFilters);
  }, []);

  // Initialize filters for ANDA DataTable
  useEffect(() => {
    const initialAndaFilters: Record<string, { value: unknown; matchMode: string }> = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    // Register date filter for lastSubmissionDate column
    FilterService.register('custom_lastSubmissionDate', dateRangeFilterFunction);

    // Initialize filters for each ANDA column (matching MoleculeSearch ANDAData)
    initialAndaFilters['applicationNumber'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['marketStatus'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['activeIngredient'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['dosageForm'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['route'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['strength'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['rld'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['rs'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['applicantHolder'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['lastSubmissionType'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    initialAndaFilters['lastSubmissionDate'] = { value: null, matchMode: FilterMatchMode.CUSTOM };

    setAndaFilters(initialAndaFilters);
  }, []);

  useEffect(() => {
    const resizeAndDraw = () => {
      if (andaTimelineRef.current) {
        const canvas = andaTimelineRef.current;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Set canvas width to match container for full-width display
          const container = canvas.parentElement;
          if (container) {
            const containerWidth = container.clientWidth - 32; // Account for padding
            canvas.width = Math.max(containerWidth, 800);
          }

          if (effectiveTimeline && effectiveTimeline.length > 0) {
            drawAndaTimeline(ctx, effectiveTimeline);
          } else {
            // Clear canvas if no data
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          }
        }
      }
    };

    resizeAndDraw();

    // Handle window resize
    window.addEventListener('resize', resizeAndDraw);
    return () => window.removeEventListener('resize', resizeAndDraw);
  }, [effectiveTimeline, hasCompetitorData]);

  // Handle pagination - uses split ANDA endpoint with current filters (server-side)
  const getAndaFilterPayload = useCallback(() => getAndaFilterParams(andaFilters), [andaFilters]);

  const handlePreviousPage = async () => {
    const gfcParam = searchParams.get('gfc');
    if (gfcParam && currentPage > 1) {
      await productAnalysisStore.setAndaPageSplit(
        gfcParam,
        currentPage - 1,
        pageSize,
        getAndaFilterPayload()
      );
    }
  };

  const handleNextPage = async () => {
    const gfcParam = searchParams.get('gfc');
    if (gfcParam && currentPage < totalPages) {
      await productAnalysisStore.setAndaPageSplit(
        gfcParam,
        currentPage + 1,
        pageSize,
        getAndaFilterPayload()
      );
    }
  };

  /** DataTable lazy onPage: PrimeReact uses 0-based page index; page/rows may be undefined */
  const handleAndaPageChange = useCallback(
    (e: { page?: number; rows?: number }) => {
      const gfcParam = searchParams.get('gfc');
      if (!gfcParam) return;
      const p = e.page ?? 0;
      const rows = e.rows ?? pageSize;
      productAnalysisStore.setAndaPageSplit(
        gfcParam,
        p + 1,
        rows,
        getAndaFilterPayload()
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, pageSize, getAndaFilterPayload]
  );

  // When ANDA filters change, debounce and fetch page 1 with new filters (server-side)
  useEffect(() => {
    if (!gfc?.trim()) return;
    if (andaFilterFirstRunRef.current) {
      andaFilterFirstRunRef.current = false;
      return;
    }
    const payload = getAndaFilterParams(andaFilters);
    const timer = setTimeout(() => {
      productAnalysisStore.fetchCompetitorsAnda({
        gfc: gfc.trim(),
        page: 1,
        page_size: pageSize,
        ...payload,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [andaFilters, gfc, pageSize, productAnalysisStore]);

  const drawAndaTimeline = (ctx: CanvasRenderingContext2D, timeline: TimelineEntry[]) => {
    const canvas = ctx.canvas;
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    if (timeline.length === 0) {
      timelinePointsRef.current = [];
      return;
    }

    // Calculate dynamic sizing based on canvas width
    const padding = Math.max(80, width * 0.06);
    const circleRadius = 12;
    const lineY = height / 2 + 10;

    // Store point positions for hover detection
    const points: TimelinePoint[] = [];

    // Draw timeline line
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(padding, lineY);
    ctx.lineTo(width - padding, lineY);
    ctx.stroke();

    // Draw events
    timeline.forEach((event, index) => {
      const x =
        timeline.length === 1
          ? width / 2
          : padding + (index / Math.max(1, timeline.length - 1)) * (width - padding * 2);

      // Store point for hover detection
      points.push({ x, y: lineY, radius: circleRadius + 4, event });

      // Draw outer glow/shadow for hover effect hint
      ctx.fillStyle = `${event.color}30`;
      ctx.beginPath();
      ctx.arc(x, lineY, circleRadius + 4, 0, 2 * Math.PI);
      ctx.fill();

      // Draw circle
      ctx.fillStyle = event.color;
      ctx.beginPath();
      ctx.arc(x, lineY, circleRadius, 0, 2 * Math.PI);
      ctx.fill();

      // Add white border to circles
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw year below the line - always visible
      ctx.fillStyle = '#374151';
      ctx.font = 'bold 13px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(event.year.toString(), x, lineY + 35);
    });

    // Store points for hover detection
    timelinePointsRef.current = points;
  };

  // Handle mouse move on canvas for tooltip
  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = andaTimelineRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const mouseX = (e.clientX - rect.left) * scaleX;
      const mouseY = (e.clientY - rect.top) * scaleY;

      // Check if mouse is over any point
      let hoveredPoint: TimelinePoint | null = null;
      for (const point of timelinePointsRef.current) {
        const distance = Math.sqrt(Math.pow(mouseX - point.x, 2) + Math.pow(mouseY - point.y, 2));
        if (distance <= point.radius + 5) {
          hoveredPoint = point;
          break;
        }
      }

      if (hoveredPoint) {
        // Calculate the screen position of the hovered point
        // Convert canvas coordinates back to screen coordinates
        const pointScreenX = rect.left + (hoveredPoint.x / scaleX);
        const pointScreenY = rect.top + (hoveredPoint.y / scaleY);

        // Position tooltip above the point, centered horizontally
        const tooltipX = pointScreenX;
        const tooltipY = pointScreenY - 20; // Position above the point

        setTooltipData({
          visible: true,
          x: tooltipX,
          y: tooltipY,
          event: hoveredPoint.event,
        });
        canvas.style.cursor = 'pointer';
      } else {
        setTooltipData(prev => (prev.visible ? { ...prev, visible: false } : prev));
        canvas.style.cursor = 'default';
      }
    },
    []
  );

  // Handle mouse leave on canvas
  const handleCanvasMouseLeave = useCallback(() => {
    setTooltipData(prev => (prev.visible ? { ...prev, visible: false } : prev));
  }, []);

  const getRankingBadge = (ranking: number) => {
    const badges = {
      1: 'first',
      2: 'second',
      3: 'third',
      4: 'fourth',
      5: 'fifth',
      6: 'sixth',
    };
    return badges[ranking as keyof typeof badges] || 'others';
  };

  const dateRangeFilterTemplate = (options: { value: [Date | null, Date | null] | null; filterApplyCallback: (value: [Date | null, Date | null]) => void }) => {
    const [from, to] = options.value ?? [null, null];

    return React.createElement(
      'div',
      {
        className: 'flex gap-1',
        style: { display: 'flex', gap: '10px', flexDirection: 'row', width: '100%' },
      },
      React.createElement(Calendar, {
        value: from,
        onChange: (e: { value: Date | Date[] | null | undefined }) => options.filterApplyCallback([e.value as Date | null, to]),
        placeholder: 'From',
        dateFormat: 'mm/dd/yy',
        showIcon: true,
        className: 'p-column-filter',
        style: { width: '10rem' },
      }),
      React.createElement(Calendar, {
        value: to,
        onChange: (e: { value: Date | Date[] | null | undefined }) => options.filterApplyCallback([from, e.value as Date | null]),
        placeholder: 'To',
        dateFormat: 'mm/dd/yy',
        showIcon: true,
        className: 'p-column-filter',
        style: { width: '10rem' },
      })
    );
  };

  return {
    // State
    filters,
    setFilters,
    andaFilters,
    setAndaFilters,

    // Data
    effectiveCompetitorData,
    effectiveAndaData,
    effectiveTimeline,
    isLoading,
    shouldShowLoader,
    hasCompetitorData,
    hasAndaData,
    competitorsStore,

    // Section-specific loading states (for split API)
    isOverviewLoading,
    isAndaLoading,
    isMarketChartsLoading,
    shouldShowOverviewLoader,
    shouldShowAndaLoader,
    shouldShowMarketChartsLoader,

    // AI Insights
    aiInsights,
    isInsightsLoading,
    insightsError,
    refetchInsights: fetchAIInsights,

    // Pagination
    currentPage,
    totalPages,
    totalCount,
    pageSize,
    startItem,
    endItem,
    handlePreviousPage,
    handleNextPage,
    handleAndaPageChange,

    // Refs
    andaTimelineRef,

    // Timeline tooltip
    tooltipData,
    handleCanvasMouseMove,
    handleCanvasMouseLeave,

    // Utility functions
    getRankingBadge,
    dateRangeFilterTemplate,

    // Chart state and data
    chartDateRange,
    tempDateRange,
    calendarViewDates,
    selectedCompany,
    marketSalesTrendData,
    filteredMarketSalesTrendData,
    marketShareByVolume,
    marketTrendsChartData,

    // Chart handlers
    handleDateRangeChange,
    handleApplyDateFilter,
    handleCalendarViewDateChange,
    setCalendarViewDates,
    handleMarketShareClick,
  };
};

export default useCompetitorsTab;
